import { Drawer } from '@blueprintjs/core'
import cx from 'classnames'
import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import BuildingIcon from '~/components/icons/BuildingIcon'
import CellIcon from '~/components/icons/CellIcon'
import CultureIcon from '~/components/icons/CultureIcon'
import InstrumentIcon from '~/components/icons/InstrumentIcon'
import JankyDeviceIcon from '~/components/icons/JankyDeviceIcon'
import MenuIcon from '~/components/icons/MenuIcon'
import UploadIcon from '~/components/icons/UploadIcon'
import ZoomInIcon from '~/components/icons/ZoomInIcon'
import Logo from '~/components/Logo'
import UserMenu from '~/core/UserMenu'
import DemoIndicator from '~/demoControls/DemoIndicator'
import LeftNavDebugInfo from './LeftNavDebugInfo'
import cs from './left_nav.scss'
import { MultiFrontendContext } from './MultiFrontendContext'

function LeftNav({ frontendContext }: { frontendContext: MultiFrontendContext }) {
  const [navOpen, setNavOpen] = useState(false)

  const automationFrontend = frontendContext.orgMetadata.automationFrontend || ''
  const cloudFrontend = frontendContext.cloudFrontend || ''

  const isPageEnabledForOrg = page => {
    const enabledPages = frontendContext.orgMetadata.enabledPages
    if (!enabledPages) return false
    return enabledPages === 'all' || enabledPages.includes(page)
  }

  const renderLink = (label, link, icon) => (
    <NavLink
      exact
      activeClassName={cs.active}
      to={link}
      className={cs.navLinkContainer}
      onClick={() => setNavOpen(false)}
    >
      <div className={cs.iconContainer}>{icon}</div>
      <div className={cs.label}>{label}</div>
    </NavLink>
  )
  const renderUrlLink = (label, link, icon) => (
    <a href={link} className={cs.navLinkContainer}>
      <div className={cs.iconContainer}>{icon}</div>
      <div className={cs.label}>{label}</div>
    </a>
  )

  const renderDemoLinks = () => (
    <>
      {(() => {
        if (frontendContext.deploymentMode === 'hybrid') {
          return
        }
        return (
          <>
            {isPageEnabledForOrg('cle-2025') &&
              renderLink(
                'iPSC Engineering',
                '/cle-2025',
                <CellIcon className={cs.navLinkIconFill} />,
              )}
            {isPageEnabledForOrg('cell_line_engineering') &&
              renderLink(
                'Cell Line Engineering',
                '/cell-line-engineering',
                <CellIcon className={cs.navLinkIconFill} />,
              )}
            {isPageEnabledForOrg('organoid_demo') &&
              renderLink(
                'Live Organoids',
                '/organoids',
                <ZoomInIcon className={cs.navLinkIconFill} />,
              )}
          </>
        )
      })()}
    </>
  )

  const renderAutomationLinks = () => (
    <>
      {/* Workcell Link */}
      {isPageEnabledForOrg('workcell') &&
        frontendContext.deploymentMode === 'cloud' &&
        automationFrontend &&
        renderUrlLink(
          'Workcell',
          `${automationFrontend}/workcell`,
          <BuildingIcon className={cs.navLinkIconFill} />,
        )}

      {isPageEnabledForOrg('workcell') &&
        frontendContext.deploymentMode === 'automation' &&
        renderLink(
          'Workcell',
          '/workcell',
          <BuildingIcon className={cs.navLinkIconFill} />,
        )}

      {/* Instrument Links */}
      {isPageEnabledForOrg('instruments') &&
        frontendContext.deploymentMode === 'cloud' &&
        automationFrontend &&
        renderUrlLink(
          'Instruments',
          `${automationFrontend}/local-instruments`,
          <JankyDeviceIcon className={cs.navLinkIconFill} />,
        )}
      {isPageEnabledForOrg('instruments') &&
        frontendContext.deploymentMode === 'automation' &&
        renderLink(
          'Instruments',
          '/local-instruments',
          <JankyDeviceIcon className={cs.navLinkIconFill} />,
        )}
    </>
  )

  const renderCloudLinks = () => (
    <>
      {isPageEnabledForOrg('monitor') &&
        frontendContext.deploymentMode === 'cloud' &&
        renderLink(
          'Cultures',
          '/cultures',
          <CultureIcon className={cs.navLinkIconFill} />,
        )}
      {isPageEnabledForOrg('monitor') &&
        frontendContext.deploymentMode === 'automation' &&
        cloudFrontend &&
        renderUrlLink(
          'Cultures',
          `${cloudFrontend}/cultures`,
          <CultureIcon className={cs.navLinkIconFill} />,
        )}
    </>
  )

  const renderOnPremLinks = () => {
    if (frontendContext.deploymentMode === 'on_prem_agent') {
      return (
        <>
          {renderLink(
            'Data Ingestors',
            '/data-ingestors',
            <InstrumentIcon className={cs.navLinkIconFill} />,
          )}
          {renderLink(
            'Datasets',
            '/datasets',
            <UploadIcon className={cs.navLinkIconFill} />,
          )}
        </>
      )
    }
  }

  const renderLocalDevelopmentLinks = () => {
    if (frontendContext.deploymentMode === 'hybrid') {
      return (
        <>
          {renderLink(
            'Cultures',
            '/cultures',
            <CultureIcon className={cs.navLinkIconFill} />,
          )}
          {isPageEnabledForOrg('cle-2025') &&
            renderLink(
              'iPSC Engineering',
              '/cle-2025',
              <CellIcon className={cs.navLinkIconFill} />,
            )}
          {isPageEnabledForOrg('cell_line_engineering') &&
            renderLink(
              'Cell Line Engineering',
              '/cell-line-engineering',
              <CellIcon className={cs.navLinkIconFill} />,
            )}
          {isPageEnabledForOrg('organoid_demo') &&
            renderLink(
              'Live Organoids',
              '/organoids',
              <ZoomInIcon className={cs.navLinkIconFill} />,
            )}

          {automationFrontend &&
            renderLink(
              'Workcell',
              `${automationFrontend}/workcell`,
              <BuildingIcon className={cs.navLinkIconFill} />,
            )}
          {automationFrontend &&
            renderUrlLink(
              'Instruments',
              `${automationFrontend}/local-instruments`,
              <JankyDeviceIcon className={cs.navLinkIconFill} />,
            )}
        </>
      )
    }
  }

  const renderLinks = () => {
    return (
      <>
        {renderLocalDevelopmentLinks()}
        {renderOnPremLinks()}
        {renderCloudLinks()}
        {renderDemoLinks()}
        {renderAutomationLinks()}
      </>
    )
  }

  const renderLeftNav = () => {
    return (
      <>
        <NavLink to='/' className={cs.logoContainer}>
          <Logo className={cs.logo} />
        </NavLink>

        {renderLinks()}

        <div className={cs.fill} />

        <LeftNavDebugInfo />
        <DemoIndicator />

        <UserMenu frontendContext={frontendContext} />
      </>
    )
  }

  return (
    <div className={cs.leftNavWrapper}>
      <div className={cx(cs.leftNav, cs.desktop)}>{renderLeftNav()}</div>
      <div className={cs.mobile}>
        <div className={cs.mobileNav}>
          <MenuIcon className={cs.icon} onClick={() => setNavOpen(true)} />
          <NavLink to='/' className={cs.logoContainer}>
            <Logo className={cs.logo} />
          </NavLink>
        </div>
        <Drawer
          isOpen={navOpen}
          onClose={() => setNavOpen(false)}
          position='left'
          size={200}
        >
          <div className={cs.leftNav}>{renderLeftNav()}</div>
        </Drawer>
      </div>
    </div>
  )
}

export default LeftNav
